import { FC } from 'react';
import classNames from 'classnames';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  OpmFirstLastWaterfallDictionaryDto,
  FirstLastTranche,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import styles from '../../opm-summary-waterfall-erf.module.scss';

interface TrancheCalculationConditionRowProps {
  valueKey: keyof NonNullable<FirstLastTranche['conditions'][number]>;
  label: string;
  trancheIndex: number;
  bold?: boolean;
  conditionIndex?: number;
  opmWaterfall: OpmFirstLastWaterfallDictionaryDto;
  selectedYear: string;
  selectedInstrument: string;
  waterfallIterations: number;
  getTrancheDictionary: (firstOrLastFive: 'first' | 'last') => Dictionary<FirstLastTranche>[];
}

const TrancheCalculationConditionRow: FC<TrancheCalculationConditionRowProps> = ({
  valueKey,
  label,
  trancheIndex,
  bold = false,
  conditionIndex,
  opmWaterfall,
  selectedYear,
  selectedInstrument,
  waterfallIterations,
  getTrancheDictionary,
}) => {
  const tranches = opmWaterfall[selectedYear].first[1].instruments[selectedInstrument].tranches;
  const trancheNames = Object.keys(tranches);
  const tranche = tranches[trancheNames[trancheIndex]];

  const condition =
    typeof conditionIndex === 'number'
      ? tranche.conditions[conditionIndex]
      : tranche.conditions.find(
          (c) => c.metric === 'InstitutionalMoM' || c.metric === 'InstitutionalIRR'
        );

  if (!condition) {
    return null;
  }

  const getCondition = (
    dict: Dictionary<FirstLastTranche>,
    trancheIndex: number,
    conditionIndex?: number
  ) => {
    const thisTrancheName = Object.keys(dict)[trancheIndex];
    const thisTranche = dict[thisTrancheName];
    const cond =
      typeof conditionIndex === 'number'
        ? thisTranche.conditions[conditionIndex]
        : thisTranche.conditions.find(
            (c) => c.metric === 'InstitutionalMoM' || c.metric === 'InstitutionalIRR'
          );
    return cond;
  };

  return (
    <tr key={`condition-row-${valueKey}-${trancheIndex}-${conditionIndex}`}>
      <td className={classNames(styles['border-right'], bold && styles['bold-text'])}>{label}</td>
      {[...getTrancheDictionary('first'), ...getTrancheDictionary('last')].map((dict, index) => {
        const conditionDetails = getCondition(dict, trancheIndex, conditionIndex);
        const value = conditionDetails?.[valueKey] ?? null;
        return (
          <td
            key={`condition-row-value-${valueKey}-${trancheIndex}-${conditionIndex}-${index}`}
            className={classNames({
              [styles['border-right']]: index + 1 === waterfallIterations,
            })}
            data-testid={`conditionRow-${valueKey}-tranche${
              trancheIndex + 1
            }-condition${conditionIndex}-simulation-${index}`}>
            <CellValue value={typeof value === 'object' ? null : value} {...amountRules} />
          </td>
        );
      })}
    </tr>
  );
};

export default TrancheCalculationConditionRow;
